import { useState } from "react";
import { AlertCircle, Loader2, Radio, ShieldCheck, Users } from "lucide-react";

import type { ShiftRota } from "./types";

/**
 * The screen before the wall.
 *
 * Supervision used to start on page load, so "has the tab open" and "is supervising" were
 * the same state. That is workable for one operator and wrong for several: capacity nobody
 * declared cannot be divided, and a background tab counted as an available supervisor.
 *
 * Showing the handover here is the other half. Dropping somebody straight onto a wall of
 * red tells them something is wrong but not what happened before they arrived.
 */
export function ShiftLobby({
  rota,
  busy,
  error,
  comfortableLoad,
  onComfortableLoadChange,
  onStart,
}: {
  rota: ShiftRota | null;
  busy: boolean;
  error: string | null;
  comfortableLoad: number;
  onComfortableLoadChange: (value: number) => void;
  onStart: () => void;
}) {
  const [touched, setTouched] = useState(false);
  const handover = rota?.handover;
  const others = rota?.on_shift ?? [];

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      <div className="w-full max-w-xl rounded-xl border border-line bg-elevated p-6">
        <div className="flex items-center gap-2 text-ink">
          <ShieldCheck size={18} className="text-brand" />
          <h1 className="text-base font-semibold">Start supervising</h1>
        </div>
        <p className="mt-1 text-xs text-ink-3">
          Live calls are not watched until you go on shift. Whoever is on shares the floor.
        </p>

        {/* Who is already on. Named rather than counted: "2 supervisors" does not tell you
            whether the person who knows this campaign is one of them. */}
        <section className="mt-5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-3">
            <Users size={12} />
            On shift now
          </div>
          {others.length ? (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {others.map((shift) => (
                <li
                  key={shift.user_id}
                  className="rounded-full border border-line bg-canvas px-2.5 py-1 text-xs text-ink-2"
                >
                  {shift.display_name || shift.user_id}
                  <span className="ml-1 text-ink-3">· {shift.comfortable_load}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-ink-3">
              Nobody. You would be the only supervisor on this account.
            </p>
          )}
        </section>

        {handover ? (
          <section className="mt-5">
            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-3">
              Handover
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-2">
              <Stat label="Live calls" value={handover.live_calls} />
              <Stat label="Open alerts" value={handover.open_alerts} />
              <Stat
                label="Overdue"
                value={handover.overdue_alerts}
                tone={handover.overdue_alerts > 0 ? "danger" : undefined}
              />
            </dl>
            {handover.held_calls.length ? (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] text-warning">
                <Radio size={12} className="mt-0.5 shrink-0" />
                {handover.held_calls.length} call
                {handover.held_calls.length === 1 ? " is" : "s are"} mid-intervention and cannot
                simply be left.
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="mt-5">
          <label
            htmlFor="comfortable-load"
            className="text-[11px] font-medium uppercase tracking-wide text-ink-3"
          >
            Calls you can comfortably watch
          </label>
          <input
            id="comfortable-load"
            type="number"
            min={1}
            max={25}
            value={Math.max(1, Math.min(25, comfortableLoad))}
            onChange={(event) => {
              setTouched(true);
              // Clamped to 1..25 as maximum visible calls limit is 25
              const parsed = Number(event.target.value);
              const safe = Number.isFinite(parsed) ? Math.round(parsed) : 1;
              onComfortableLoadChange(Math.max(1, Math.min(25, safe)));
            }}
            className="mt-1.5 w-24 rounded-lg border border-line bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-brand"
            data-testid="supervision-comfortable-load"
          />
          <p className="mt-1 text-[11px] text-ink-3">
            {touched ? "Affects the number of live call cards visible to you (1 to 25)." : "Shared with the floor so calls are split in proportion."}
          </p>
        </section>

        {error ? (
          <p className="mt-4 flex items-center gap-1.5 text-xs text-danger">
            <AlertCircle size={13} />
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={onStart}
          disabled={busy}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-hover disabled:opacity-50"
          data-testid="supervision-start-shift"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {busy ? "Starting…" : "Start supervising"}
        </button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "danger";
}) {
  return (
    <div className="rounded-lg border border-line bg-canvas px-3 py-2">
      <dt className="text-[10px] uppercase tracking-wide text-ink-3">{label}</dt>
      <dd className={tone === "danger" ? "text-lg font-semibold text-danger" : "text-lg font-semibold text-ink"}>
        {value}
      </dd>
    </div>
  );
}
